import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Map, { Layer, Marker, Source, useMap, MapRef } from 'react-map-gl';
import type { Feature, FeatureCollection, LineString, MultiPolygon, Point, Polygon } from 'geojson';
import 'mapbox-gl/dist/mapbox-gl.css';
import '../../style/AreaMap.css';
import '../../style/AreaEditor.css'; // For .area-vertex-label
import { HEALTH_STATUS_CONFIG, HealthStatus } from '../../helpers/reports/areaHealthStatus';
import SlamMapOverlay, { SlamOverlayData, SlamPlacement } from './SlamMapOverlay';

type AreaGeometry = Polygon | MultiPolygon | Point | null | undefined;

interface MapPoint {
    id?: string | number;
    lat: number;
    lng: number;
    label?: string;
    selected?: boolean;
    headingDegrees?: number | null;
    directionMode?: 'pattern' | 'auto' | 'manual' | null;
}

interface MapPath {
    id?: string | number;
    coordinates: Array<{ lat: number; lng: number }>;
    selected?: boolean;
    warning?: boolean;
    previewOnly?: boolean;
}

interface AreaMapProps {
    areaGeometry?: AreaGeometry;
    areaName?: string | null;
    measurements?: MapPoint[];
    points?: MapPoint[];
    paths?: MapPath[];
    initialViewState?: {
        longitude: number;
        latitude: number;
        zoom: number;
        bearing?: number;
        pitch?: number;
    };
    mapKey?: string | number;
    allAreas?: Array<{ id: number; geom?: any; name?: string; areaType?: { id: number; name: string } | null; hierarchyDepth?: number }>;
    areaType?: { id: number; name: string } | null;
    templateImageUrl?: string | null;
    areaTypeTemplates?: Map<number, string>;
    /** Set the initial map bearing in degrees (clockwise from north). E.g. pattern bearing to align first row W→E. */
    initialBearing?: number | null;
    disableAutoFit?: boolean;
    onPointClick?: (pointId: string | number) => void;
    onMeasurementClick?: (measurementId: string | number) => void;
    selectedAreaId?: number | string | null;
    areaStatusById?: Record<number, HealthStatus>;
    pointLabelPosition?: 'center' | 'above';
    focusPoint?: { lat: number; lng: number; zoom?: number } | null;
    alwaysShowMap?: boolean;
    emptyOverlayMessage?: string;
    onViewStateChange?: (view: { lat: number; lng: number; zoom: number }) => void;
    draggablePointIds?: Array<string | number>;
    onPointDragEnd?: (pointId: string | number, location: { lat: number; lng: number }) => void;
    /** SLAM map overlay data for indoor/mixed areas */
    slamOverlay?: SlamOverlayData | null;
    /** Placement for unanchored SLAM overlays */
    slamPlacement?: SlamPlacement | null;
}

const DEFAULT_VIEW = { longitude: -0.1278, latitude: 51.5074, zoom: 13, bearing: 0, pitch: 0 };

const MAPBOX_TOKEN =
    process.env.REACT_APP_MAPBOX_TOKEN ||
    'pk.eyJ1IjoiZS1uYW5vIiwiYSI6ImNtamM0cjZzcjBtMW8zZnNiZ2I4NmljYTIifQ.MP4bn1hVGwhOXujMC4wjUw';

// Hierarchy-based color palette
const HIERARCHY_COLORS = [
    { fill: '#2f54eb', fillOpacity: 0.2, line: '#1d39c4' },  // Level 0: Blue
    { fill: '#52c41a', fillOpacity: 0.2, line: '#389e0d' },  // Level 1: Green
    { fill: '#fa8c16', fillOpacity: 0.2, line: '#d46b08' },  // Level 2: Orange
    { fill: '#eb2f96', fillOpacity: 0.2, line: '#c41d7f' },  // Level 3: Pink
    { fill: '#722ed1', fillOpacity: 0.2, line: '#531dab' },  // Level 4: Purple
    { fill: '#13c2c2', fillOpacity: 0.2, line: '#08979c' },  // Level 5: Cyan
];

const getColorForDepth = (depth: number = 0) => {
    return HIERARCHY_COLORS[depth % HIERARCHY_COLORS.length];
};

const getRingCoordinates = (geom: any): number[][] => {
    if (!geom || !geom.coordinates) return [];
    if (geom.type === 'Polygon') {
        return geom.coordinates[0] || [];
    }
    if (geom.type === 'MultiPolygon') {
        return geom.coordinates[0]?.[0] || [];
    }
    // Point, LineString, or unknown — not renderable as a polygon ring
    return [];
};

/** Returns true if the geometry can be drawn as a polygon on the map */
const isPolygonGeometry = (geom: any): boolean =>
    !!geom && (geom.type === 'Polygon' || geom.type === 'MultiPolygon');

const computeCentroid = (coords: number[][]): [number, number] | null => {
    if (!coords || coords.length < 3) return null;
    let area = 0;
    let cx = 0;
    let cy = 0;
    for (let i = 0; i < coords.length - 1; i += 1) {
        const [x0, y0] = coords[i];
        const [x1, y1] = coords[i + 1];
        const cross = x0 * y1 - x1 * y0;
        area += cross;
        cx += (x0 + x1) * cross;
        cy += (y0 + y1) * cross;
    }
    area *= 0.5;
    if (area === 0) {
        const valid = coords.filter((c) => Array.isArray(c) && c.length === 2);
        if (!valid.length) return null;
        const avg = valid.reduce(
            (acc, [x, y]) => [acc[0] + x, acc[1] + y],
            [0, 0]
        );
        return [avg[0] / valid.length, avg[1] / valid.length];
    }
    return [cx / (6 * area), cy / (6 * area)];
};

const getDirectionArrowClassName = (point: MapPoint) => {
    const classNames = ['map-point-direction-arrow'];

    if (point.directionMode) {
        classNames.push(`map-point-direction-arrow-${point.directionMode}`);
    }

    if (point.selected) {
        classNames.push('map-point-direction-arrow-selected');
    }

    return classNames.join(' ');
};

const MissionPointMarker: React.FC<{
    point: MapPoint;
    pointLabelPosition: 'center' | 'above';
    onClick?: () => void;
}> = ({ point, pointLabelPosition, onClick }) => (
    <div className={pointLabelPosition === 'above' && point.label ? 'map-point-label-wrapper map-point-marker-stack' : 'map-point-marker-stack'}>
        {pointLabelPosition === 'above' && point.label ? (
            <div className="map-point-label">{point.label}</div>
        ) : null}
        <div className="map-point-marker-anchor">
            {Number.isFinite(point.headingDegrees) ? (
                <div
                    className={getDirectionArrowClassName(point)}
                    style={{ transform: `translateX(-50%) rotate(${Number(point.headingDegrees)}deg)` }}
                />
            ) : null}
            <div
                className="pattern-point-marker"
                onClick={onClick}
                style={{
                    minWidth: 24,
                    height: 24,
                    padding: '0 4px',
                    borderRadius: 999,
                    background: point.selected ? '#52c41a' : '#fa8c16',
                    border: '3px solid white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 'bold',
                    color: 'white',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    cursor: onClick ? 'pointer' : 'default',
                    transition: 'all 0.2s ease',
                }}
                title={point.label || 'Mission point'}
            >
                {pointLabelPosition === 'above' ? null : point.label}
            </div>
        </div>
    </div>
);

const AreaMap: React.FC<AreaMapProps> = ({
    areaGeometry,
    areaName,
    measurements = [],
    points = [],
    paths = [],
    initialViewState,
    mapKey,
    allAreas,
    areaType,
    templateImageUrl,
    areaTypeTemplates,
    initialBearing,
    disableAutoFit,
    onPointClick,
    onMeasurementClick,
    selectedAreaId,
    areaStatusById,
    pointLabelPosition = 'center',
    focusPoint = null,
    alwaysShowMap = false,
    emptyOverlayMessage,
    onViewStateChange,
    draggablePointIds,
    onPointDragEnd,
    slamOverlay,
    slamPlacement,
}) => {
    // Detect Point geometry for indoor areas
    const isPointGeom = areaGeometry?.type === 'Point';
    const pointCoords = useMemo<[number, number] | null>(() => {
        if (!areaGeometry || areaGeometry.type !== 'Point') return null;
        const coords = areaGeometry.coordinates as [number, number];
        return coords && coords.length >= 2 ? coords : null;
    }, [areaGeometry]);

    const areaFeature = useMemo<Feature<Polygon | MultiPolygon> | null>(() => {
        if (!areaGeometry || !isPolygonGeometry(areaGeometry)) return null;
        return {
            type: 'Feature',
            geometry: areaGeometry as Polygon | MultiPolygon,
            properties: { name: areaName || 'Area' },
        };
    }, [areaGeometry, areaName]);

    // Show all areas (parent + children) if provided
    const allAreasFeatureCollection = useMemo<FeatureCollection | null>(() => {
        if (!allAreas || allAreas.length === 0) return null;
        return {
            type: 'FeatureCollection',
            features: allAreas.filter(a => a.geom).map(a => ({
                type: 'Feature',
                geometry: a.geom,
                properties: { id: a.id, name: a.name || '' }
            }))
        };
    }, [allAreas]);

    const labelPoints = useMemo<FeatureCollection<Point> | null>(() => {
        const features: Feature<Point>[] = [];
        if (allAreas && allAreas.length > 0) {
            allAreas.forEach((area) => {
                if (!area.geom) return;
                const ring = getRingCoordinates(area.geom);
                const centroid = computeCentroid(ring);
                if (!centroid) return;
                features.push({
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: centroid },
                    properties: { id: area.id, name: area.name || '' },
                });
            });
        } else if (areaGeometry) {
            const ring = getRingCoordinates(areaGeometry);
            const centroid = computeCentroid(ring);
            if (centroid) {
                features.push({
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: centroid },
                    properties: { id: 'primary', name: areaName || 'Area' },
                });
            }
        }
        if (!features.length) return null;
        return { type: 'FeatureCollection', features };
    }, [allAreas, areaGeometry, areaName]);

    const pointFeatures = useMemo<FeatureCollection<Point> | null>(() => {
        if (!points.length) return null;
        return {
            type: 'FeatureCollection',
            features: points.map((p) => ({
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
                properties: {
                    id: p.id ?? `${p.lat}-${p.lng}`,
                    label: p.label,
                    selected: p.selected,
                },
            })),
        };
    }, [points]);

    const pathFeatures = useMemo<FeatureCollection<LineString> | null>(() => {
        if (!paths.length) return null;
        const features = paths
            .map((path) => {
                const coordinates = path.coordinates
                    .filter((coordinate) => Number.isFinite(coordinate.lat) && Number.isFinite(coordinate.lng))
                    .map((coordinate) => [coordinate.lng, coordinate.lat] as [number, number]);

                if (coordinates.length < 2) {
                    return null;
                }

                return {
                    type: 'Feature',
                    geometry: {
                        type: 'LineString',
                        coordinates,
                    },
                    properties: {
                        id: path.id ?? coordinates.map((coordinate) => coordinate.join(',')).join('|'),
                        selected: Boolean(path.selected),
                        warning: Boolean(path.warning),
                        previewOnly: Boolean(path.previewOnly),
                    },
                } as Feature<LineString>;
            })
            .filter(Boolean) as Array<Feature<LineString>>;

        if (!features.length) {
            return null;
        }

        return {
            type: 'FeatureCollection',
            features,
        };
    }, [paths]);

    const firstMeasurement = measurements.find((m) => typeof m.lat === 'number' && typeof m.lng === 'number');

    const computedViewState = useMemo(() => {
        const bearing = initialBearing != null ? initialBearing : (initialViewState?.bearing ?? DEFAULT_VIEW.bearing);
        // Respect explicit initial view first (e.g., selected measurement focus)
        if (initialViewState) {
            return { ...DEFAULT_VIEW, ...initialViewState, bearing };
        }
        // Handle Point geometry (indoor areas)
        if (pointCoords) {
            return { ...DEFAULT_VIEW, longitude: pointCoords[0], latitude: pointCoords[1], zoom: 18, bearing };
        }
        if (areaGeometry && isPolygonGeometry(areaGeometry) && 'coordinates' in areaGeometry && areaGeometry.coordinates.length) {
            const coords = areaGeometry.type === 'Polygon'
                ? (areaGeometry as Polygon).coordinates[0]
                : (areaGeometry as MultiPolygon).coordinates[0][0];
            if (coords && coords.length) {
                const lon = coords.reduce((sum: number, c: any) => sum + c[0], 0) / coords.length;
                const lat = coords.reduce((sum: number, c: any) => sum + c[1], 0) / coords.length;
                return { ...DEFAULT_VIEW, longitude: lon, latitude: lat, zoom: 15, bearing };
            }
        }
        if (firstMeasurement) {
            return { ...DEFAULT_VIEW, longitude: firstMeasurement.lng, latitude: firstMeasurement.lat, zoom: 15, bearing };
        }
        return { ...DEFAULT_VIEW, bearing };
    }, [areaGeometry, pointCoords, firstMeasurement, initialViewState, initialBearing]);

    const mapRef = useRef<MapRef>(null);
    const lastFitBoundsKeyRef = useRef<string | null>(null);
    const [mapReady, setMapReady] = useState(false);
    const draggablePointIdSet = useMemo(
        () => new Set((draggablePointIds || []).map((value) => String(value))),
        [draggablePointIds]
    );

    const syncViewStateFromMap = useCallback(() => {
        if (!onViewStateChange) return;
        const map = mapRef.current;
        if (!map) return;
        const center = map.getCenter();
        onViewStateChange({
            lat: center.lat,
            lng: center.lng,
            zoom: map.getZoom(),
        });
    }, [onViewStateChange]);
    
    const hasData = !!areaFeature || !!pointCoords || !!firstMeasurement || (pointFeatures && pointFeatures.features.length > 0) || (pathFeatures && pathFeatures.features.length > 0);
    const shouldRenderMap = hasData || alwaysShowMap;

    // Extract vertices from ALL areas (primary + children) for numbered markers
    const allVertices = useMemo(() => {
        const extractVertices = (geom: any, areaId: number | string) => {
            if (!geom || !geom.coordinates) return [];
            // Only Polygon / MultiPolygon have ring coordinates — skip Point etc.
            if (geom.type !== 'Polygon' && geom.type !== 'MultiPolygon') return [];
            const coords = geom.type === 'Polygon' 
                ? geom.coordinates[0] 
                : geom.coordinates[0][0];
            
            // Remove closing coordinate if duplicate
            const trimmed = coords.length > 1 && 
                coords[0][0] === coords[coords.length - 1][0] && 
                coords[0][1] === coords[coords.length - 1][1]
                ? coords.slice(0, -1)
                : coords;
            
            return trimmed
                .filter((c: any) => c && c[0] !== undefined && c[1] !== undefined && c[0] !== null && c[1] !== null)
                .map((c: any, idx: number) => ({
                    id: `vertex-${areaId}-${idx}`,
                    lng: c[0],
                    lat: c[1],
                    label: String(idx + 1)
                }));
        };

        // If we have allAreas, extract vertices from all of them
        if (allAreas && allAreas.length > 0) {
            return allAreas.flatMap(area => 
                area.geom ? extractVertices(area.geom, area.id) : []
            );
        }
        
        // Otherwise, just use the primary area geometry
        if (areaGeometry) {
            return extractVertices(areaGeometry, 'primary');
        }
        
        return [];
    }, [areaGeometry, allAreas]);

    // Extract vertices from primary area only (for template overlay - needs exactly 4 vertices)
    const primaryVertices = useMemo(() => {
        if (!areaGeometry || !areaGeometry.coordinates) return [];
        // Guard against Point / non-polygon geometries
        if (!isPolygonGeometry(areaGeometry)) return [];
        const coords = areaGeometry.type === 'Polygon' 
            ? (areaGeometry as Polygon).coordinates[0] 
            : (areaGeometry as MultiPolygon).coordinates[0][0];
        
        // Remove closing coordinate if duplicate
        const trimmed = coords.length > 1 && 
            coords[0][0] === coords[coords.length - 1][0] && 
            coords[0][1] === coords[coords.length - 1][1]
            ? coords.slice(0, -1)
            : coords;
        
        return trimmed
            .filter((c: any) => c && c[0] !== undefined && c[1] !== undefined && c[0] !== null && c[1] !== null)
            .map((c: any, idx: number) => ({
                id: `vertex-primary-${idx}`,
                lng: c[0],
                lat: c[1],
                label: String(idx + 1)
            }));
    }, [areaGeometry]);

    const fitMapToData = useCallback(() => {
        if (disableAutoFit || focusPoint) return;
        const map = mapRef.current;
        if (!map) return;

        const allCoords: number[][] = [];

        if (allAreas && allAreas.length > 0) {
            allAreas.forEach(area => {
                if (!area.geom?.coordinates) return;
                if (isPolygonGeometry(area.geom)) {
                    const coords = area.geom.type === 'Polygon' 
                        ? area.geom.coordinates[0] 
                        : area.geom.coordinates[0][0];
                    allCoords.push(...coords);
                } else if (area.geom.type === 'Point') {
                    // Include Point geometries (indoor areas)
                    const [lng, lat] = area.geom.coordinates as [number, number];
                    if (Number.isFinite(lng) && Number.isFinite(lat)) {
                        allCoords.push([lng, lat]);
                    }
                }
            });
        } else if (areaGeometry?.coordinates) {
            if (isPolygonGeometry(areaGeometry)) {
                const coords = areaGeometry.type === 'Polygon'
                    ? (areaGeometry as Polygon).coordinates[0]
                    : (areaGeometry as MultiPolygon).coordinates[0][0];
                allCoords.push(...(coords as any));
            } else if (areaGeometry.type === 'Point') {
                const [lng, lat] = areaGeometry.coordinates as [number, number];
                if (Number.isFinite(lng) && Number.isFinite(lat)) {
                    allCoords.push([lng, lat]);
                }
            }
        }

        if (allCoords.length === 0) {
            measurements.forEach((m) => {
                if (typeof m.lat === 'number' && typeof m.lng === 'number') {
                    allCoords.push([m.lng, m.lat]);
                }
            });
            points.forEach((p) => {
                if (typeof p.lat === 'number' && typeof p.lng === 'number') {
                    allCoords.push([p.lng, p.lat]);
                }
            });
            paths.forEach((path) => {
                path.coordinates.forEach((coordinate) => {
                    if (typeof coordinate.lat === 'number' && typeof coordinate.lng === 'number') {
                        allCoords.push([coordinate.lng, coordinate.lat]);
                    }
                });
            });
        }

        if (allCoords.length > 0) {
            const boundsKey = allCoords
                .map((c) => `${c[0].toFixed(6)},${c[1].toFixed(6)}`)
                .join('|');
            if (boundsKey === lastFitBoundsKeyRef.current) {
                return;
            }
            lastFitBoundsKeyRef.current = boundsKey;
            const lngs = allCoords.map(c => c[0]);
            const lats = allCoords.map(c => c[1]);
            const minLng = Math.min(...lngs);
            const maxLng = Math.max(...lngs);
            const minLat = Math.min(...lats);
            const maxLat = Math.max(...lats);

            // Single point or zero-extent bounds → flyTo instead of fitBounds
            if (Math.abs(maxLng - minLng) < 1e-7 && Math.abs(maxLat - minLat) < 1e-7) {
                map.flyTo({
                    center: [minLng, minLat],
                    zoom: 18,
                    duration: 800,
                });
            } else {
                map.fitBounds(
                    [[minLng, minLat], [maxLng, maxLat]],
                    { padding: { top: 50, bottom: 50, left: 50, right: 50 }, duration: 800 }
                );
            }
        }
    }, [allAreas, areaGeometry, disableAutoFit, focusPoint, measurements, paths, points]);

    useEffect(() => {
        if (!mapReady) return;
        fitMapToData();
    }, [fitMapToData, mapReady]);

    useEffect(() => {
        if (!mapReady || !focusPoint) return;
        const map = mapRef.current;
        if (!map) return;
        map.flyTo({
            center: [focusPoint.lng, focusPoint.lat],
            zoom: focusPoint.zoom ?? 15,
            duration: 800,
        });
    }, [mapReady, focusPoint]);

    useEffect(() => {
        // Reset readiness when the map key changes so style-load hooks re-run.
        setMapReady(false);
        lastFitBoundsKeyRef.current = null;
    }, [mapKey, areaName]);

    useEffect(() => {
        if (!mapReady) return;
        const mapInstance = mapRef.current?.getMap();
        if (!mapInstance || typeof mapInstance.hasImage !== 'function') return;

        const ensureLabelImage = () => {
            if (mapInstance.hasImage('area-label-bg')) return;

            const size = 64;
            const canvas = document.createElement('canvas');
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            const radius = 10;
            ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
            ctx.strokeStyle = 'rgba(15, 23, 42, 0.25)';
            ctx.lineWidth = 2;

            ctx.beginPath();
            ctx.moveTo(radius, 0);
            ctx.lineTo(size - radius, 0);
            ctx.quadraticCurveTo(size, 0, size, radius);
            ctx.lineTo(size, size - radius);
            ctx.quadraticCurveTo(size, size, size - radius, size);
            ctx.lineTo(radius, size);
            ctx.quadraticCurveTo(0, size, 0, size - radius);
            ctx.lineTo(0, radius);
            ctx.quadraticCurveTo(0, 0, radius, 0);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();

            const imageData = ctx.getImageData(0, 0, size, size);
            mapInstance.addImage('area-label-bg', {
                width: size,
                height: size,
                data: imageData.data,
            });
        };

        ensureLabelImage();
        const handleStyleImageMissing = (event: any) => {
            if (event?.id === 'area-label-bg') {
                ensureLabelImage();
            }
        };
        mapInstance.on('styledata', ensureLabelImage);
        mapInstance.on('styleimagemissing', handleStyleImageMissing);
        return () => {
            mapInstance.off('styledata', ensureLabelImage);
            mapInstance.off('styleimagemissing', handleStyleImageMissing);
        };
    }, [mapReady]);

    const templateOverlays = useMemo(() => {
        if (!allAreas || !areaTypeTemplates) return [];
        return allAreas
            .map((area) => {
                const templateUrl = area.areaType?.id ? areaTypeTemplates.get(area.areaType.id) : null;
                if (!templateUrl || !area.geom) return null;
                // Only Polygon / MultiPolygon have ring coords — skip Point etc.
                if (!isPolygonGeometry(area.geom)) return null;

                const areaCoords = area.geom.type === 'Polygon'
                    ? area.geom.coordinates[0]
                    : area.geom.coordinates[0][0];

                const trimmed = areaCoords.length > 1 &&
                    areaCoords[0][0] === areaCoords[areaCoords.length - 1][0] &&
                    areaCoords[0][1] === areaCoords[areaCoords.length - 1][1]
                    ? areaCoords.slice(0, -1)
                    : areaCoords;

                const areaVertices = trimmed
                    .filter((c: any) => c && c[0] !== undefined && c[1] !== undefined)
                    .map((c: any) => ({
                        lng: c[0],
                        lat: c[1],
                    }));

                const verticesKey = areaVertices
                    .slice(0, 4)
                    .map((v: { lng: number; lat: number }) => `${v.lng.toFixed(6)}:${v.lat.toFixed(6)}`)
                    .join('|');

                return {
                    areaId: area.id,
                    templateUrl,
                    vertices: areaVertices,
                    verticesKey,
                };
            })
            .filter((overlay): overlay is { areaId: number; templateUrl: string; vertices: Array<{ lng: number; lat: number }>; verticesKey: string } => !!overlay);
    }, [allAreas, areaTypeTemplates]);

    return (
        <div className="area-map-container">
            {shouldRenderMap ? (
                <>
                    <Map
                        ref={mapRef}
                        key={mapKey ?? areaName ?? 'area-map'}
                        mapboxAccessToken={MAPBOX_TOKEN}
                        mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                        initialViewState={computedViewState}
                        style={{ width: '100%', height: '100%' }}
                        onLoad={() => {
                            setMapReady(true);
                            fitMapToData();
                            syncViewStateFromMap();
                        }}
                        onMoveEnd={() => {
                            syncViewStateFromMap();
                        }}
                    >
                    {/* Template overlays for all areas with templates (lookup by area type ID) */}
                    {templateOverlays.map((overlay) => (
                        <TemplateImageOverlay
                            key={`template-${overlay.areaId}`}
                            templateImageUrl={overlay.templateUrl}
                            vertices={overlay.vertices}
                            verticesKey={overlay.verticesKey}
                            areaId={overlay.areaId}
                        />
                    ))}
                    
                    {/* Fallback: single template for primary area if no areaTypeTemplates provided */}
                    {!areaTypeTemplates && templateImageUrl && (
                        <TemplateImageOverlay 
                            templateImageUrl={templateImageUrl} 
                            vertices={primaryVertices}
                            areaId="primary"
                        />
                    )}
                    {/* Render areas with hierarchy-based colors */}
                    {allAreas && allAreas.length > 0 && allAreas.map((area) => {
                        if (!area.geom) return null;
                        const status = areaStatusById?.[area.id];
                        const statusColors = status ? HEALTH_STATUS_CONFIG[status] : null;
                        const fallbackColors = getColorForDepth(area.hierarchyDepth || 0);
                        const colors = statusColors
                            ? { fill: statusColors.mapFill, fillOpacity: 0.35, line: statusColors.mapLine }
                            : fallbackColors;
                        const isSelected = selectedAreaId !== null && selectedAreaId !== undefined && Number(selectedAreaId) === area.id;
                        const hasSelection = selectedAreaId !== null && selectedAreaId !== undefined;
                        const fillOpacity = hasSelection
                            ? (isSelected ? Math.max(colors.fillOpacity, 0.6) : Math.min(colors.fillOpacity, 0.35))
                            : colors.fillOpacity;
                        const lineWidth = hasSelection ? (isSelected ? 3 : 1) : 2;
                        const lineOpacity = hasSelection && !isSelected ? 0.5 : 1;
                        return (
                            <Source 
                                key={`area-${area.id}`}
                                id={`area-${area.id}`} 
                                type="geojson" 
                                data={{
                                    type: 'Feature',
                                    geometry: area.geom,
                                    properties: { id: area.id, name: area.name || '' }
                                }}
                            >
                                <Layer
                                    id={`area-fill-${area.id}`}
                                    type="fill"
                                    paint={{ 
                                        'fill-color': colors.fill, 
                                        'fill-opacity': fillOpacity 
                                    }}
                                />
                                <Layer
                                    id={`area-outline-${area.id}`}
                                    type="line"
                                    paint={{ 
                                        'line-color': colors.line, 
                                        'line-width': lineWidth,
                                        'line-opacity': lineOpacity,
                                    }}
                                />
                            </Source>
                        );
                    })}
                    {!allAreasFeatureCollection && areaFeature && (
                        <Source id="area-geometry" type="geojson" data={areaFeature}>
                            <Layer
                                id="area-fill"
                                type="fill"
                                paint={{ 'fill-color': '#2f54eb', 'fill-opacity': 0.2 }}
                            />
                            <Layer
                                id="area-outline"
                                type="line"
                                paint={{ 'line-color': '#1d39c4', 'line-width': 2 }}
                            />
                        </Source>
                    )}
                    {labelPoints && (
                        <Source id="area-labels" type="geojson" data={labelPoints}>
                            <Layer
                                id="area-labels-text"
                                type="symbol"
                                layout={{
                                    'text-field': ['get', 'name'],
                                    'text-size': 12,
                                    'text-anchor': 'center',
                                    'text-allow-overlap': false,
                                    'text-ignore-placement': false,
                                    'icon-image': 'area-label-bg',
                                    'icon-text-fit': 'both',
                                    'icon-text-fit-padding': [4, 6, 4, 6],
                                    'icon-allow-overlap': false,
                                }}
                                paint={{
                                    'text-color': '#0f172a',
                                }}
                            />
                        </Source>
                    )}
                    {pathFeatures && (
                        <Source id="mission-paths" type="geojson" data={pathFeatures}>
                            <Layer
                                id="mission-path-lines"
                                type="line"
                                filter={['!=', ['get', 'previewOnly'], true]}
                                layout={{
                                    'line-cap': 'round',
                                    'line-join': 'round',
                                }}
                                paint={{
                                    'line-color': [
                                        'case',
                                        ['boolean', ['get', 'warning'], false],
                                        '#ef4444',
                                        ['boolean', ['get', 'selected'], false],
                                        '#1677ff',
                                        '#94a3b8',
                                    ],
                                    'line-width': [
                                        'case',
                                        ['boolean', ['get', 'warning'], false],
                                        4,
                                        ['boolean', ['get', 'selected'], false],
                                        4,
                                        3,
                                    ],
                                    'line-opacity': 0.85,
                                }}
                            />
                            <Layer
                                id="mission-waypoint-order-lines"
                                type="line"
                                filter={['==', ['get', 'previewOnly'], true]}
                                layout={{
                                    'line-cap': 'round',
                                    'line-join': 'round',
                                }}
                                paint={{
                                    'line-color': [
                                        'case',
                                        ['boolean', ['get', 'warning'], false],
                                        '#ef4444',
                                        '#64748b',
                                    ],
                                    'line-width': [
                                        'case',
                                        ['boolean', ['get', 'warning'], false],
                                        4,
                                        2,
                                    ],
                                    'line-opacity': 0.75,
                                    'line-dasharray': [2, 2],
                                }}
                            />
                        </Source>
                    )}
                    {/* Pattern point markers with matching preview style */}
                    {points.map((p) => (
                        <Marker
                            key={p.id ?? `point-${p.lat}-${p.lng}`}
                            longitude={p.lng}
                            latitude={p.lat}
                            anchor="center"
                            draggable={p.id !== undefined && draggablePointIdSet.has(String(p.id))}
                            onDragEnd={(event: any) => {
                                if (!onPointDragEnd || p.id === undefined) {
                                    return;
                                }

                                onPointDragEnd(p.id, {
                                    lat: Number(event?.lngLat?.lat),
                                    lng: Number(event?.lngLat?.lng),
                                });
                            }}
                        >
                            <MissionPointMarker
                                point={p}
                                pointLabelPosition={pointLabelPosition}
                                onClick={onPointClick && p.id !== undefined && p.id !== null
                                    ? () => onPointClick(p.id as string | number)
                                    : undefined}
                            />
                        </Marker>
                    ))}
                    {/* Vertex markers for all areas - small black dots */}
                    {allVertices.map((v: { id: string; lng: number; lat: number; label: string }) => (
                        <Marker key={v.id} longitude={v.lng} latitude={v.lat} anchor="center">
                            <div style={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: '#000',
                                border: '1px solid #fff'
                            }} />
                        </Marker>
                    ))}
                    
                    {/* Measurement markers - interactive with selection state */}
                    {measurements.map((m) => (
                        <Marker key={m.id ?? `${m.lat}-${m.lng}`} longitude={m.lng} latitude={m.lat} anchor="center">
                            <div 
                                className={`measurement-marker ${m.selected ? 'measurement-marker-selected' : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onMeasurementClick && m.id) {
                                        onMeasurementClick(m.id);
                                    }
                                }}
                                title={m.label || 'Measurement location'}
                                style={{
                                    cursor: onMeasurementClick ? 'pointer' : 'default'
                                }}
                            />
                        </Marker>
                    ))}

                    {/* Indoor area pin marker for Point geometry */}
                    {pointCoords && (
                        <Marker longitude={pointCoords[0]} latitude={pointCoords[1]} anchor="bottom">
                            <div className="indoor-area-pin" title={areaName || 'Indoor area'}>
                                <div className="indoor-area-pin-icon">
                                    <svg width="32" height="40" viewBox="0 0 32 40" fill="none">
                                        <path d="M16 0C7.164 0 0 7.164 0 16c0 12 16 24 16 24s16-12 16-24C32 7.164 24.836 0 16 0z" fill="#1d39c4"/>
                                        <circle cx="16" cy="16" r="8" fill="white"/>
                                        <circle cx="16" cy="16" r="4" fill="#1d39c4"/>
                                    </svg>
                                </div>
                                {areaName && (
                                    <div className="indoor-area-pin-label">{areaName}</div>
                                )}
                            </div>
                        </Marker>
                    )}

                    {/* SLAM map overlay for indoor/mixed areas (read-only in detail view) */}
                    {slamOverlay && (
                        <SlamMapOverlay
                            overlayId={mapKey ?? 'slam-detail'}
                            slamData={slamOverlay}
                            placement={slamPlacement}
                            opacity={0.7}
                        />
                    )}
                    </Map>
                    {!hasData && alwaysShowMap ? (
                        <div className="area-map-overlay-empty">
                            <span>{emptyOverlayMessage || 'Select geometry or add points to preview this map.'}</span>
                        </div>
                    ) : null}
                </>
            ) : (
                <EmptyMapState />
            )}
        </div>
    );
};

const EmptyMapState: React.FC = () => (
    <div className="area-map-empty">
        <span>No geometry selected.</span>
    </div>
);

// Template Image Overlay Component
const TemplateImageOverlay: React.FC<{ 
    templateImageUrl?: string | null; 
    vertices: Array<{ lng: number; lat: number }>;
    verticesKey?: string;
    areaId: number | string;
}> = ({ templateImageUrl, vertices, verticesKey, areaId }) => {
    const { current: map } = useMap();
    const templateImageUrlRef = useRef<string | null>(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const lastAppliedRef = useRef<{ url: string | null; coordsKey: string | null }>({
        url: null,
        coordsKey: null,
    });

    // Keep ref in sync
    useEffect(() => {
        templateImageUrlRef.current = templateImageUrl || null;
    }, [templateImageUrl]);

    // Track when map style is loaded
    useEffect(() => {
        if (!map) return;
        const mapInstance = map.getMap();
        
        if (mapInstance.isStyleLoaded()) {
            setMapLoaded(true);
        } else {
            const onLoad = () => {
                setMapLoaded(true);
                mapInstance.off('load', onLoad);
            };
            mapInstance.on('load', onLoad);
            return () => {
                mapInstance.off('load', onLoad);
            };
        }
    }, [map]);

    useEffect(() => {
        if (!map || !mapLoaded) return;

        const mapInstance = map.getMap();
        if (!mapInstance || typeof mapInstance.isStyleLoaded !== 'function') {
            return;
        }
        try {
            if (!mapInstance.isStyleLoaded()) {
                return;
            }
        } catch {
            return;
        }

        const currentImageUrl = templateImageUrlRef.current;
        const usableCoords = vertices.length >= 4 ? vertices.slice(0, 4) : [];
        const coordsKey = verticesKey ?? usableCoords.map((v) => `${v.lng.toFixed(6)}:${v.lat.toFixed(6)}`).join('|');

        const layerId = `template-image-layer-${areaId}`;
        const sourceId = `template-image-${areaId}`;

        const hasOverlay = () => {
            try {
                return !!mapInstance.getLayer(layerId) && !!mapInstance.getSource(sourceId);
            } catch {
                return false;
            }
        };

        const removeOverlay = () => {
            try {
                if (mapInstance.getLayer(layerId)) {
                    mapInstance.removeLayer(layerId);
                }
                if (mapInstance.getSource(sourceId)) {
                    mapInstance.removeSource(sourceId);
                }
            } catch {
                // noop
            }
        };

        if (currentImageUrl && usableCoords.length === 4) {
            const isSame =
                lastAppliedRef.current.url === currentImageUrl &&
                lastAppliedRef.current.coordsKey === coordsKey &&
                hasOverlay();

            if (!isSame) {
                removeOverlay();

                const bounds: [[number, number], [number, number], [number, number], [number, number]] = [
                    [usableCoords[3].lng, usableCoords[3].lat],
                    [usableCoords[2].lng, usableCoords[2].lat],
                    [usableCoords[1].lng, usableCoords[1].lat],
                    [usableCoords[0].lng, usableCoords[0].lat],
                ];

                mapInstance.addSource(sourceId, {
                    type: 'image',
                    url: currentImageUrl,
                    coordinates: bounds,
                });

                mapInstance.addLayer({
                    id: layerId,
                    type: 'raster',
                    source: sourceId,
                    paint: {
                        'raster-opacity': 0.5,
                        'raster-brightness-min': 0,
                        'raster-brightness-max': 1,
                    },
                }, mapInstance.getLayer('area-labels-text') ? 'area-labels-text' : undefined);

                lastAppliedRef.current = { url: currentImageUrl, coordsKey };
            }
        } else {
            if (hasOverlay()) {
                removeOverlay();
            }
            lastAppliedRef.current = { url: null, coordsKey: null };
        }

        return () => {
            if (!mapInstance || typeof mapInstance.isStyleLoaded !== 'function') {
                return;
            }
            try {
                if (!mapInstance.isStyleLoaded()) {
                    return;
                }
            } catch {
                return;
            }
            removeOverlay();
        };
    }, [map, mapLoaded, verticesKey, templateImageUrl, areaId, vertices]);

    return null;
};

export default AreaMap;
